// BUILD-042 — Unified employee workspace. Compact contextual breadcrumbs above the workspace,
// per the Owner-approved decision's explicit follow-up ("Chris explicitly likes the breadcrumbs
// in this reference; preserve them as part of the approved visual contract"):
//   Self:           HR -> My HR -> selected section
//   Administration: HR -> Employees -> employee name -> selected section
// Parent destinations are real clickable routes; the current section is always plain text.
// Clicking a parent never forces a redundant directory/record reload merely for breadcrumb
// navigation — "Employees" and "HR" are real routes (cheap client-side nav, no extra fetch
// beyond what that route already does on mount); an employee-name crumb (when present) resets
// the in-page section back to Overview rather than re-fetching the roster.
import { Link } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  /** Real route — rendered as a Link. Omit for a crumb that only resets in-page state. */
  to?: string;
  /** In-page state reset (e.g. back to Overview) — never a network reload. */
  onClick?: () => void;
}

/** The LAST item is always the current section — rendered as plain text regardless of whether
 *  `to`/`onClick` were supplied, per the decision's "current section as plain text." */
export function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-gray-400 mb-3 flex items-center gap-1.5 flex-wrap">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={`${item.label}-${i}`} className="flex items-center gap-1.5">
            {i > 0 && <span aria-hidden="true" className="text-gray-300">/</span>}
            {isLast ? (
              <span className="text-gray-500">{item.label}</span>
            ) : item.to ? (
              <Link to={item.to} className="hover:text-[#4AABDE] hover:underline">{item.label}</Link>
            ) : item.onClick ? (
              <button type="button" onClick={item.onClick} className="hover:text-[#4AABDE] hover:underline">{item.label}</button>
            ) : (
              <span>{item.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
